import { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useAuditLog } from "@/api/hooks/auditLog";
import { useSites } from "@/api/hooks/sites";
import type { SiteAuditEvent, SiteListRow } from "@/api/types";
import { formatDateTime } from "@/utils/format";
import { formatSiteDisplayId } from "@/api/labels";

const PAGE_SIZE = 50;

/**
 * Build the "Details" cell for one audit row. Prefers, in order:
 * - validation events: extracted vs expected name + score
 * - explicit operator reason (overrides, deletes, etc.)
 * - "—" otherwise.
 */
function buildDetails(
  e: SiteAuditEvent,
  payload: {
    extracted_name?: string;
    expected_name?: string;
    score?: number;
  } | null,
): string {
  if (e.event_type.startsWith("validation.")) {
    const parts: string[] = [];
    if (payload?.extracted_name && payload?.expected_name) {
      parts.push(
        `extracted "${payload.extracted_name}" vs expected "${payload.expected_name}"`,
      );
    } else if (payload?.extracted_name) {
      parts.push(`extracted "${payload.extracted_name}"`);
    }
    if (typeof payload?.score === "number") {
      parts.push(`similarity ${payload.score}/100`);
    }
    if (e.reason) parts.push(`reason: ${e.reason}`);
    if (parts.length > 0) return parts.join(" · ");
  }
  return e.reason ?? "—";
}

export default function AuditLogPage() {
  const { data: sites, isLoading: sitesLoading } = useSites();
  const [siteFilter, setSiteFilter] = useState<SiteListRow | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack.length > 0 ? cursorStack[cursorStack.length - 1] : undefined;

  const { data, isLoading, error } = useAuditLog({
    siteId: siteFilter?.id,
    before: cursor,
    limit: PAGE_SIZE,
  });

  const siteNameById = useMemo(
    () => new Map((sites ?? []).map((s) => [s.id, s])),
    [sites],
  );

  const onSiteChange = (next: SiteListRow | null) => {
    setSiteFilter(next);
    setCursorStack([]);
  };

  const onNext = () => {
    if (data?.next_before) setCursorStack((s) => [...s, data.next_before!]);
  };
  const onPrev = () => setCursorStack((s) => s.slice(0, -1));

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          Audit log
        </Typography>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: "flex", gap: 2, alignItems: "center", py: 1.5 }}>
          <Autocomplete<SiteListRow>
            sx={{ width: 360 }}
            options={sites ?? []}
            value={siteFilter}
            onChange={(_, v) => onSiteChange(v)}
            getOptionLabel={(s) =>
              s.display_id !== null
                ? `${formatSiteDisplayId(s.display_id)} · ${s.common_name}`
                : s.common_name
            }
            isOptionEqualToValue={(a, b) => a.id === b.id}
            loading={sitesLoading}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Filter by site" placeholder="All sites" />
            )}
          />
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            Append-only record of every state-changing API call. Cursor-paginated, newest first.
          </Typography>
        </CardContent>
      </Card>

      {error && <Alert severity="error">{error.message}</Alert>}

      <Card>
        <CardContent sx={{ p: 0 }}>
          {isLoading && (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {!isLoading && data && data.items.length === 0 && (
            <Typography color="text.secondary" sx={{ p: 4, textAlign: "center" }}>
              No audit events {siteFilter ? `for ${siteFilter.common_name}` : ""}.
            </Typography>
          )}
          {!isLoading && data && data.items.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 200 }}>When</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Site</TableCell>
                  <TableCell>From → To</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((e) => {
                  const payload = e.payload as
                    | {
                        actor_name?: string;
                        actor_email?: string;
                        actor_username?: string;
                        // CV validator surfaces these in payload for
                        // validation.* events so the audit log can render
                        // the mismatch inline.
                        extracted_name?: string;
                        expected_name?: string;
                        score?: number;
                      }
                    | null;
                  const fallbackSite =
                    e.site_id && !e.site_label
                      ? siteNameById.get(e.site_id)
                      : undefined;
                  const siteRendered =
                    e.site_label ??
                    (fallbackSite
                      ? `${formatSiteDisplayId(fallbackSite.display_id)} · ${fallbackSite.common_name}`
                      : "—");
                  const actor =
                    payload?.actor_name ||
                    payload?.actor_email ||
                    payload?.actor_username ||
                    (e.actor_user_id ? e.actor_user_id.slice(0, 8) + "…" : "—");
                  const details = buildDetails(e, payload);
                  return (
                    <TableRow key={e.id} hover>
                      <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }}>
                        {formatDateTime(e.at)}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{actor}</TableCell>
                      <TableCell>{e.event_type}</TableCell>
                      <TableCell sx={{ fontSize: 13, maxWidth: 320 }}>
                        <Box>
                          <Box sx={{ fontWeight: 500 }}>
                            {e.target_label ?? "—"}
                          </Box>
                          <Box
                            sx={{
                              color: "text.secondary",
                              fontSize: 11,
                              textTransform: "capitalize",
                            }}
                          >
                            {e.entity_type.split("_").join(" ")}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{siteRendered}</TableCell>
                      <TableCell>
                        {e.from_state ?? "—"} → {e.to_state ?? "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, maxWidth: 360 }}>
                        {details}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: "flex-end" }}>
        <Button
          size="small"
          variant="outlined"
          onClick={onPrev}
          disabled={cursorStack.length === 0}
        >
          ← Newer
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={onNext}
          disabled={!data?.next_before}
        >
          Older →
        </Button>
      </Stack>
    </Box>
  );
}
